const readEntry = (entry) =>
  new Promise((resolve, reject) => {
    if (!entry) return reject(new Error("No entry"));
    if (entry.isFile) {
      entry.file(
        (file) => resolve({ file, entry }),
        (err) => reject(err),
      );
    } else {
      resolve({ file: null, entry });
    }
  });

const readDirEntries = (dirReader) =>
  new Promise((resolve, reject) => {
    const all = [];
    const readBatch = () => {
      dirReader.readEntries(
        (entries) => {
          if (!entries || entries.length === 0) {
            resolve(all);
          } else {
            all.push(...Array.from(entries));
            readBatch();
          }
        },
        (err) => reject(err),
      );
    };
    readBatch();
  });

const traverseEntry = async (entry, basePath = "", filesOut) => {
  if (!entry) return;
  if (entry.isFile) {
    const { file } = await readEntry(entry);
    if (file) {
      try {
        const relativePath = basePath
          ? `${basePath}/${file.name}`
          : file.name;
        Object.defineProperty(file, "webkitRelativePath", {
          value: relativePath,
          writable: true,
        });
      } catch (_e) {
        // Some browsers don't allow redefining; non-fatal.
      }
      filesOut.push(file);
    }
  } else if (entry.isDirectory) {
    const reader = entry.createReader();
    const entries = await readDirEntries(reader);
    const subPath = basePath ? `${basePath}/${entry.name}` : entry.name;
    for (const child of entries) {
      await traverseEntry(child, subPath, filesOut);
    }
  }
};

const getEntryFromItem = (item) => {
  if (item?.webkitGetAsEntry) {
    try {
      return item.webkitGetAsEntry();
    } catch (_e) {
      return null;
    }
  }
  return null;
};

const isUnsupportedDataTransfer = (dataTransfer) =>
  !dataTransfer ||
  !dataTransfer.items ||
  typeof dataTransfer.items[0]?.webkitGetAsEntry !== "function";

const processDroppedItems = async (dataTransfer) => {
  const items = Array.from(dataTransfer.items || []);
  const filesFromList = Array.from(dataTransfer.files || []);

  if (isUnsupportedDataTransfer(dataTransfer)) {
    return {
      folders: [],
      looseFiles: filesFromList,
      allFiles: filesFromList,
      supported: filesFromList.length > 0,
    };
  }

  const looseFiles = [];
  const folders = [];
  const folderEntries = [];

  for (const item of items) {
    if (item.kind !== "file") continue;
    const entry = getEntryFromItem(item);
    if (entry && entry.isDirectory) {
      folderEntries.push(entry);
    } else {
      const file = item.getAsFile();
      if (file) looseFiles.push(file);
    }
  }

  for (const entry of folderEntries) {
    const folderName = entry.name;
    const folderFiles = [];
    await traverseEntry(entry, "", folderFiles);
    folders.push({ name: folderName, files: folderFiles });
  }

  return {
    folders,
    looseFiles,
    allFiles: [...looseFiles, ...folders.flatMap((f) => f.files)],
    supported: true,
  };
};

export { processDroppedItems, traverseEntry };

export default { processDroppedItems, traverseEntry };